import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Users, Zap } from 'lucide-react';
import { format } from 'date-fns';

interface CostLog {
  id: string;
  user_id: string;
  created_at: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  estimated_cost_usd: string | number;
  success: boolean;
  username?: string;
}

interface CostStats {
  totalCost: number;
  totalRequests: number;
  averageCost: number;
  totalTokens: number;
  costToday: number;
  requestsToday: number;
  costThisMonth: number;
}

export function AICostTracker() {
  const [logs, setLogs] = useState<CostLog[]>([]);
  const [stats, setStats] = useState<CostStats>({
    totalCost: 0,
    totalRequests: 0,
    averageCost: 0,
    totalTokens: 0,
    costToday: 0,
    requestsToday: 0,
    costThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCostData();
  }, []);

  const fetchCostData = async () => {
    setLoading(true);
    try {
      // Fetch recent logs with user info
      const { data: logsData, error: logsError } = await supabase
        .from('ai_recommendation_logs')
        .select(`
          *
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) throw logsError;

      // Fetch usernames separately
      const userIds = [...new Set(logsData?.map(log => log.user_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p.username]) || []);

      // Enrich logs with usernames
      const enrichedLogs = logsData?.map(log => ({
        ...log,
        username: profilesMap.get(log.user_id) || 'unknown'
      })) || [];

      // Calculate stats
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const allLogs = enrichedLogs;
      const todayLogs = allLogs.filter(log => new Date(log.created_at) >= todayStart);
      const monthLogs = allLogs.filter(log => new Date(log.created_at) >= monthStart);

      setStats({
        totalCost: allLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
        totalRequests: allLogs.length,
        averageCost: allLogs.length > 0 
          ? allLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0) / allLogs.length 
          : 0,
        totalTokens: allLogs.reduce((sum, log) => sum + log.total_tokens, 0),
        costToday: todayLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
        requestsToday: todayLogs.length,
        costThisMonth: monthLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
      });

      setLogs(allLogs as any);
    } catch (error) {
      console.error('Error fetching cost data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Total Cost
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.totalCost.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              This Month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.costThisMonth.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(new Date(), 'MMMM yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRequests}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.requestsToday} today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Avg Cost/Request
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.averageCost.toFixed(5)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              ~{Math.round(stats.totalTokens / Math.max(stats.totalRequests, 1))} tokens
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent API Calls</CardTitle>
          <CardDescription>
            Last 50 AI recommendation requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No API calls logged yet
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">@{log.username || 'unknown'}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(log.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {log.model} • {log.total_tokens.toLocaleString()} tokens
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-medium">
                        ${Number(log.estimated_cost_usd).toFixed(5)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {log.prompt_tokens.toLocaleString()} → {log.completion_tokens.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}